'use client';

import { Button, Skeleton } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { PaymentInstrumentIcon } from '../../../../../../PaymentInstrumentIcon/PaymentInstrumentIcon';
import type { PaymentMethod } from '../../../../types';

function MaskedNumber() {
  return (
    <div className="flex gap-1 items-center">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="w-1.5 h-1.5 bg-foreground rounded-full"></div>
      ))}
    </div>
  );
}

type PaymentMethodItemProps = PaymentMethod & {
  namespace: string;
  isLoading: boolean;
  onDelete: () => void;
  'data-track-container'?: string;
  'data-track-method'?: string;
};

function PaymentMethodItemIcon({ type }: { type: string }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-foreground" data-testid="payment-method-item-icon" role="img" aria-label={type}>
      <PaymentInstrumentIcon type={type} />
    </span>
  );
}

function PaymentMethodItem({
  number,
  type,
  namespace,
  isLoading,
  onDelete,
  'data-track-container': dataTrackContainer,
  'data-track-method': dataTrackMethod,
}: PaymentMethodItemProps) {
  const t = useTranslations(namespace);

  return (
    <div className="flex items-center gap-3 border border-border rounded-lg p-4" data-track-container={dataTrackContainer} data-track-method={dataTrackMethod}>
      {isLoading ? (
        <Skeleton className="h-6 w-full" />
      ) : (
        <>
          <PaymentMethodItemIcon type={type} />
          <div className="flex gap-1.5 w-full">
            <MaskedNumber />
            <div className="grow paragraph-strong">{number}</div>
          </div>
          <Button
            variant="secondary"
            size="s"
            onClick={onDelete}
            aria-label={t('delete_button_aria_label', { number })}
            data-track-name="Delete"
            data-track-method={type}
            data-testid="payment-method-item-delete-button"
          >
            {t('delete_button')}
          </Button>
        </>
      )}
    </div>
  );
}

export { PaymentMethodItem, type PaymentMethodItemProps };
